import { faCircleChevronLeft, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
	ACCESS_RIGHT_TYPES,
	checkPermission,
} from "../../../constant/permissions";
import {
	ButtonAtom,
	DatePickerAtom,
	DropdownAtom,
	TextFieldAtom,
} from "../../atoms/atom";
import "./filter-bar.scss";

function FilterBar(props) {
	const { t } = useTranslation();
	const { items, onFilterAction, area, canAdd } = props;

	// Use Redux for auth state instead of AuthContext
	const { user } = useSelector((state) => state.auth) || {};
	const permissions = user?.permissions || [];

	const hasAddPermission = () => {
		if (typeof canAdd === "boolean") {
			return canAdd;
		}
		if (!area) {
			return true;
		}
		return checkPermission(permissions, area, ACCESS_RIGHT_TYPES.CREATE);
	};

	// Check if the user has permission for a specific action button
	const hasActionPermission = (itemKey) => {
		if (itemKey === "add" || !area) {
			return hasAddPermission();
		}
		// For custom action buttons, use the same permission check as for "add"
		return checkPermission(permissions, area, ACCESS_RIGHT_TYPES.CREATE);
	};

	const getSearchFilter = (index, itemInfo) => {
		return (
			<TextFieldAtom
				type="search"
				placeholder={
					itemInfo?.labelKey
						? t(itemInfo.labelKey)
						: t("filter-bar.search")
				}
				value={itemInfo?.value}
				onChange={(e) =>
					onFilterAction(index, itemInfo?.key, {
						value: e.target.value,
					})
				}
				suffix={<FontAwesomeIcon icon={faMagnifyingGlass} style={{ color: "#94a3b8", fontSize: "12px" }} />}
			/>
		);
	};

	const getButton = (index, itemInfo) => {
		if (!hasActionPermission(itemInfo.key)) {
			return null;
		}

		// Display text with a "+" prefix for add buttons
		const buttonText = itemInfo.labelKey
			? t(itemInfo.labelKey)
			: t(`filter-bar.${itemInfo.key || "add"}`);

		const displayText =
			itemInfo.key === "add" ? `+ ${buttonText}` : buttonText;

		const btnClassName =
			itemInfo.className ||
			(itemInfo.variant
				? `btn btn-${itemInfo.variant}`
				: "filter-bar-btn");

		// Get tooltip text - use disabledReason if button is disabled, otherwise use default behavior
		const tooltipText =
			itemInfo.disabled && itemInfo.disabledReason
				? itemInfo.disabledReason.includes(".")
					? t(itemInfo.disabledReason)
					: itemInfo.disabledReason
				: undefined;

		const button = (
			<ButtonAtom
				className={btnClassName}
				disabled={itemInfo.disabled || false}
				onClick={(e) => {
					e && e.stopPropagation();
					e && e.preventDefault();
					onFilterAction(index, itemInfo?.key);
				}}
				variant={itemInfo.variant || "primary"}
			>
				{displayText}
			</ButtonAtom>
		);

		// Wrap disabled buttons with React Bootstrap tooltip for better UX
		if (itemInfo.disabled && tooltipText) {
			return (
				<OverlayTrigger
					placement="top"
					delay={{ show: 250, hide: 100 }}
					overlay={
						<Tooltip id={`tooltip-${itemInfo.key}-${index}`}>
							{tooltipText}
						</Tooltip>
					}
				>
					{({ ref, ...triggerHandler }) => (
						<div
							{...triggerHandler}
							ref={ref}
							className="fb-disabled-wrapper"
						>
							{button}
						</div>
					)}
				</OverlayTrigger>
			);
		}

		return button;
	};

	const getDropDown = (index, itemInfo) => {
		return (
			<DropdownAtom
				placeholder={
					itemInfo.labelKey
						? t(itemInfo.labelKey)
						: t("filter-bar.dropdown.select")
				}
				value={itemInfo?.value}
				options={itemInfo?.options}
				sortOptions={itemInfo.sortOptions}
				hideNoOptionSelect={itemInfo.hideNoOptionSelect}
				shouldTranslate={itemInfo.shouldTranslate}
				onChange={(e) =>
					onFilterAction(index, itemInfo?.key, {
						value: e.target.value,
					})
				}
				isClearable
			/>
		);
	};

	const getChipGroup = (index, itemInfo) => {
		const label = itemInfo.labelKey ? t(itemInfo.labelKey) : null;

		return (
			<div
				className="filter-chip-group"
				role="group"
				aria-label={label || undefined}
			>
				{label && (
					<span className="filter-chip-group__label">{label}</span>
				)}
				<div className="filter-chip-group__chips">
					{(itemInfo.options || []).map((opt) => {
						const selected = itemInfo.value === opt.value;
						return (
							<button
								key={opt.value}
								type="button"
								className={`filter-chip${selected ? " filter-chip--active" : ""}`}
								aria-pressed={selected}
								onClick={() =>
									onFilterAction(index, itemInfo.key, {
										value: opt.value,
									})
								}
								disabled={itemInfo.disabled}
							>
								{opt.labelKey ? t(opt.labelKey) : opt.label}
							</button>
						);
					})}
				</div>
			</div>
		);
	};

	// Toggle filter with switch and labels on both sides
	const getToggle = (index, itemInfo) => {
		// If two options, render as switch with labels
		if (itemInfo.options.length === 2) {
			const [left, right] = itemInfo.options;
			return (
				<div className="d-flex align-items-center filter-toggle-switch">
					<span className="me-2 fw-bold">{left.label}</span>
					<Form.Check
						type="switch"
						checked={itemInfo.value === right.value}
						onChange={(e) =>
							itemInfo.onChange(
								e.target.checked ? right.value : left.value,
							)
						}
						className="mx-1 d-flex"
						disabled={itemInfo.disabled}
					/>
					<span className="ms-2 fw-bold">{right.label}</span>
				</div>
			);
		}
		// Fallback to button group for more than two options
		return (
			<div className="filter-toggle-group">
				{itemInfo.options.map((opt) => (
					<button
						key={opt.value}
						type="button"
						className={`btn btn-sm ${itemInfo.value === opt.value ? "btn-primary" : "btn-outline-secondary"} mx-1`}
						onClick={() => itemInfo.onChange(opt.value)}
						disabled={itemInfo.disabled}
					>
						{opt.label}
					</button>
				))}
			</div>
		);
	};

	const getDatePicker = (index, itemInfo) => {
		return (
			<DatePickerAtom
				placeholder={
					itemInfo.labelKey
						? t(itemInfo.labelKey)
						: t("filter-bar.date.select")
				}
				value={itemInfo.value}
				onChange={(e) =>
					onFilterAction(index, itemInfo?.key, { value: e })
				}
			/>
		);
	};

	const getBackButton = (index, itemInfo) => {
		return (
			<div onClick={() => onFilterAction(index, itemInfo?.key)}>
				<FontAwesomeIcon
					icon={faCircleChevronLeft}
					className="fa-xl me-1"
					title={t("filter-bar.back")}
				/>
				{t("filter-bar.back")}
			</div>
		);
	};

	// Add custom component handler
	const getCustomComponent = (index, itemInfo) => {
		if (!hasActionPermission(itemInfo.key)) {
			return null;
		}

		// Get tooltip text for custom components
		const tooltipText =
			itemInfo.disabled && itemInfo.disabledReason
				? itemInfo.disabledReason.includes(".")
					? t(itemInfo.disabledReason)
					: itemInfo.disabledReason
				: undefined;

		// Wrap custom components with tooltip if disabled and tooltip text exists
		if (itemInfo.disabled && tooltipText) {
			return (
				<OverlayTrigger
					placement="top"
					delay={{ show: 250, hide: 100 }}
					overlay={
						<Tooltip id={`tooltip-custom-${itemInfo.key}-${index}`}>
							{tooltipText}
						</Tooltip>
					}
				>
					{({ ref, ...triggerHandler }) => (
						<div
							{...triggerHandler}
							ref={ref}
							className="fb-disabled-wrapper"
						>
							{itemInfo.component}
						</div>
					)}
				</OverlayTrigger>
			);
		}

		// Render the custom component directly
		return itemInfo.component;
	};

	// Dropdown button with multiple action options
	const getDropdownButton = (index, itemInfo) => {
		if (!hasActionPermission(itemInfo.key)) {
			return null;
		}

		const buttonText = itemInfo.labelKey
			? t(itemInfo.labelKey)
			: t(`filter-bar.${itemInfo.key || "actions"}`);

		const variant = itemInfo.variant || "success";

		return (
			<Dropdown className="filter-dropdown-button">
				<Dropdown.Toggle
					variant={variant}
					id={`dropdown-${itemInfo.key}-${index}`}
					disabled={itemInfo.disabled || false}
				>
					{buttonText}
				</Dropdown.Toggle>

				<Dropdown.Menu>
					{(itemInfo.options || []).map((option, optIdx) => (
						<Dropdown.Item
							key={optIdx}
							onClick={() =>
								onFilterAction(index, option.key || option.value, {
									value: option.value,
									data: option.data,
								})
							}
							disabled={option.disabled || false}
						>
							{option.icon && (
								<i className={`${option.icon} me-2`}></i>
							)}
							{option.labelKey ? t(option.labelKey) : option.label}
						</Dropdown.Item>
					))}
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	// Separate action buttons from other items
	const actionButtons = items.filter(
		(item) => item.key === "add" || item.displayPosition === "right",
	);

	const otherItems = items.filter(
		(item) => item.key !== "add" && item.displayPosition !== "right",
	);

	return (
		<div className="d-flex filterbar light">
			<div className="d-flex filterbar-container flex-grow-1">
				{otherItems.map((item, idx) => {
					const originalIndex = items.findIndex((i) => i === item);
					return (
						<div
							className="filter-item"
							key={idx}
						>
							{item.type === "backButton" &&
								getBackButton(originalIndex, item)}
							{item.type === "search" &&
								getSearchFilter(originalIndex, item)}
							{item.type === "button" &&
								getButton(originalIndex, item)}
							{item.type === "dropdown" &&
								getDropDown(originalIndex, item)}
							{item.type === "chipGroup" &&
								getChipGroup(originalIndex, item)}
							{item.type === "datepicker" &&
								getDatePicker(originalIndex, item)}
							{item.type === "custom" &&
								getCustomComponent(originalIndex, item)}
						</div>
					);
				})}
			</div>

			{/* Action buttons rendered on the right side */}
			{actionButtons.length > 0 && (
				<div className="d-flex ms-auto">
					{actionButtons.map((item, idx) => {
						const originalIndex = items.findIndex(
							(i) => i === item,
						);
						return (
							<div
								className="filter-item"
								key={`action-btn-${idx}`}
							>
								{item.type === "button" &&
									getButton(originalIndex, item)}
								{item.type === "dropdownButton" &&
									getDropdownButton(originalIndex, item)}
								{item.type === "custom" &&
									getCustomComponent(originalIndex, item)}
								{item.type === "toggle" &&
									getToggle(originalIndex, item)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}

export default FilterBar;
